import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FraudShieldBadge } from './FraudShieldBadge';
import {
  Shield, ShieldCheck, Clock, Truck, FileCheck, AlertTriangle,
  CheckCircle, XCircle, Calendar, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const FraudShieldPanel = ({ userId, isOwnProfile = false }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFraudShieldProfile();
  }, [userId]);

  const loadFraudShieldProfile = async () => {
    try {
      const endpoint = isOwnProfile 
        ? '/fraud-shield/my-profile' 
        : `/fraud-shield/profile/${userId}`;
      const response = await api.get(endpoint);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load fraud shield profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#12151a] border-[#1a1d24]">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#4ADE80] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card className="bg-[#12151a] border-[#1a1d24]" data-testid="fraud-shield-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Shield className="w-5 h-5 text-[#4ADE80]" />
            Fraud Shield Status
          </div>
          <FraudShieldBadge status={profile.fraud_shield_status} size="md" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trust Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Account Age */}
          <div className="bg-[#0a0c10] rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <Clock className="w-3 h-3" />
              Account Age
            </div>
            <div className="text-lg font-semibold text-white">
              {profile.account_age_days} days
            </div>
          </div>
          
          {/* Completed Loads */}
          <div className="bg-[#0a0c10] rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <Truck className="w-3 h-3" />
              Completed Loads
            </div>
            <div className="text-lg font-semibold text-white">
              {profile.completed_loads}
            </div>
          </div>
          
          {/* Verification Status */}
          <div className="bg-[#0a0c10] rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <ShieldCheck className="w-3 h-3" />
              Verified
            </div>
            <div className="flex items-center gap-1">
              {profile.is_verified ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">Yes</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500 font-medium">No</span>
                </>
              )}
            </div>
          </div>
          
          {/* Documents */}
          <div className="bg-[#0a0c10] rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <FileCheck className="w-3 h-3" />
              Docs Approved
            </div>
            <div className="flex items-center gap-1">
              {profile.documents_approved ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">Yes</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500 font-medium">No</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reports Summary */}
        {(profile.total_reports > 0 || profile.unresolved_reports > 0) && (
          <div className="bg-[#0a0c10] rounded-lg p-3 border border-yellow-500/20">
            <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
              <AlertTriangle className="w-4 h-4" />
              Report History
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-semibold text-white">{profile.total_reports}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-yellow-400">{profile.unresolved_reports}</div>
                <div className="text-xs text-gray-500">Open</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-green-400">{profile.resolved_reports}</div>
                <div className="text-xs text-gray-500">Resolved</div>
              </div>
            </div>
          </div>
        )}

        {/* Safety Review Date */}
        {profile.last_safety_review_at && (
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Last Safety Review
            </div>
            <span>{formatDate(profile.last_safety_review_at)}</span>
          </div>
        )}

        {/* Clear Status Message */}
        {profile.fraud_shield_status === 'clear' && profile.total_reports === 0 && (
          <div className="text-center py-2">
            <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
              <ShieldCheck className="w-4 h-4" />
              No safety concerns on record
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FraudShieldPanel;
